/**
 * QuoteSessionTracker - Tracks user sessions from quote creation start to completion
 * Includes tracking of away time when user switches tabs/windows and idle time when no interactions >30s
 */
export class QuoteSessionTracker {
  private static readonly SESSION_STORAGE_KEY = 'quoteSessionData';
  private static readonly IDLE_THRESHOLD = 30000; // 30 seconds in milliseconds
  private static isTracking = false;
  private static awayStartTime: number | null = null;
  private static totalAwayTime = 0;
  private static tabSwitchCount = 0;
  private static lastActivityTime = Date.now();
  private static totalIdleTime = 0;
  private static idleCheckInterval: NodeJS.Timeout | null = null;

  public static startSession(): string {
    const sessionStartTime = new Date().toISOString();
    
    // Get existing data to preserve editQuoteSessionTime if it exists
    const existingData = this.getSessionData();
    
    const sessionData = {
      sessionStartTime,
      sessionEndTime: null,
      awayTime: 0,
      idleTime: 0,
      tabsCount: 0,
      // Preserve editQuoteSessionTime if it exists from before starting the session
      editQuoteSessionTime: existingData?.editQuoteSessionTime
    };
    
    console.log('🔍 DEBUG: startSession - preserving existing editQuoteSessionTime:', existingData?.editQuoteSessionTime);
    console.log('🔍 DEBUG: startSession - new sessionData:', sessionData);
    
    // Store in session storage so it persists across page navigations
    sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    
    // Reset counters
    this.totalAwayTime = 0;
    this.totalIdleTime = 0;
    this.tabSwitchCount = 0;
    this.lastActivityTime = Date.now();
    
    // Start visibility and idle tracking
    this.startVisibilityTracking();
    this.startIdleTracking();
    
    console.log('🕐 Quote Session Started:', sessionStartTime);
    return sessionStartTime;
  }

  public static endSession(): string {
    const sessionEndTime = new Date().toISOString();
    const existingData = this.getSessionData();
    
    // Stop visibility and idle tracking
    this.stopVisibilityTracking();
    this.stopIdleTracking();
    
    // If user is currently away, add the final away time
    if (this.awayStartTime) {
      this.totalAwayTime += Date.now() - this.awayStartTime;
      this.awayStartTime = null;
    }
    
    if (existingData) {
      const updatedData = {
        ...existingData,
        sessionEndTime,
        awayTime: this.totalAwayTime / 1000, // Convert milliseconds to seconds
        idleTime: this.totalIdleTime / 1000, // Convert milliseconds to seconds
        tabsCount: this.tabSwitchCount
      };
      
      // Calculate productive time (total session time - away time - idle time)
      if (updatedData.sessionStartTime && updatedData.sessionEndTime) {
        const start = new Date(updatedData.sessionStartTime);
        const end = new Date(updatedData.sessionEndTime);
        const totalTimeMs = end.getTime() - start.getTime();
        const totalTimeSeconds = totalTimeMs / 1000;
        const awayTimeSeconds = this.totalAwayTime / 1000;
        const idleTimeSeconds = this.totalIdleTime / 1000;
        updatedData.productiveTime = Math.max(0, totalTimeSeconds - awayTimeSeconds - idleTimeSeconds); // Ensure non-negative
      }
      
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updatedData));
      
      console.log('🕑 Quote Session Ended:', sessionEndTime);
      console.log('🚪 Total Away Time:', (this.totalAwayTime / 1000).toFixed(2), 'seconds');
      console.log('😴 Total Idle Time:', (this.totalIdleTime / 1000).toFixed(2), 'seconds');
      console.log('⚡ Productive Time:', (updatedData.productiveTime || 0).toFixed(2), 'seconds');
      console.log('🔄 Total Tab Switches:', this.tabSwitchCount);
    }
    
    return sessionEndTime;
  }

  public static getSessionData(): { sessionStartTime: string; sessionEndTime: string | null; totalSessionTime?: number; awayTime?: number; idleTime?: number; productiveTime?: number; tabsCount?: number; fillQuoteSessionEndtimeInSeconds?: number; searchProductsAndApplyCartEndtimeSessionInSeconds?: number; editQuoteSessionTime?: number } | null {
    try {
      const data = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (data) {
        const parsedData = JSON.parse(data);
        // Calculate total session time if both start and end times are available
        if (parsedData.sessionStartTime && parsedData.sessionEndTime) {
          const start = new Date(parsedData.sessionStartTime);
          const end = new Date(parsedData.sessionEndTime);
          const totalTimeMs = end.getTime() - start.getTime();
          parsedData.totalSessionTime = totalTimeMs / 1000; // Convert to seconds
          
          // Calculate productive time (total session time - away time - idle time)
          const awayTimeSeconds = parsedData.awayTime || 0; // Already in seconds
          const idleTimeSeconds = parsedData.idleTime || 0; // Already in seconds
          parsedData.productiveTime = Math.max(0, parsedData.totalSessionTime - awayTimeSeconds - idleTimeSeconds);
        }
        return parsedData;
      }
      return null;
    } catch (error) {
      console.error('Error retrieving session data:', error);
      return null;
    }
  }

  public static clearSession(): void {
    this.stopVisibilityTracking();
    this.stopIdleTracking();
    this.totalAwayTime = 0;
    this.totalIdleTime = 0;
    this.awayStartTime = null;
    sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
    console.log('🧹 Quote Session Cleared');
  }

  public static getSessionDuration(): number | null {
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.sessionStartTime && sessionData.sessionEndTime) {
      const start = new Date(sessionData.sessionStartTime);
      const end = new Date(sessionData.sessionEndTime);
      return (end.getTime() - start.getTime()) / 1000; // Duration in seconds
    }
    return null;
  }

  public static getTotalAwayTime(): number {
    return this.totalAwayTime;
  }

  public static setFillQuoteSessionEnd(): number {
    const existingData = this.getSessionData();
    if (existingData && existingData.sessionStartTime) {
      const fillQuoteEndTime = new Date().toISOString();
      const startTime = new Date(existingData.sessionStartTime);
      const endTime = new Date(fillQuoteEndTime);
      const fillQuoteSessionDurationInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
      
      const updatedData = {
        ...existingData,
        fillQuoteSessionEndtimeInSeconds: fillQuoteSessionDurationInSeconds
      };
      
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updatedData));
      
      console.log('🎯 Fill Quote Session Ended:', fillQuoteEndTime);
      console.log('🎯 Fill Quote Duration:', fillQuoteSessionDurationInSeconds.toFixed(2), 'seconds');
      
      return fillQuoteSessionDurationInSeconds;
    }
    return 0;
  }

  public static setSearchProductsAndApplyCartEnd(): number {
    const existingData = this.getSessionData();
    if (existingData && existingData.sessionStartTime) {
      const searchCartEndTime = new Date().toISOString();
      const startTime = new Date(existingData.sessionStartTime);
      const endTime = new Date(searchCartEndTime);
      const searchProductsAndApplyCartEndtimeSessionInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
      
      const updatedData = {
        ...existingData,
        searchProductsAndApplyCartEndtimeSessionInSeconds: searchProductsAndApplyCartEndtimeSessionInSeconds
      };
      
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updatedData));
      
      console.log('🛒 Search Products and Apply Cart Session Ended:', searchCartEndTime);
      console.log('🛒 Search Products and Apply Cart Duration:', searchProductsAndApplyCartEndtimeSessionInSeconds.toFixed(2), 'seconds');
      
      return searchProductsAndApplyCartEndtimeSessionInSeconds;
    }
    return 0;
  }

  public static setEditQuoteSessionTime(): number {
    console.log('🔍 DEBUG: setEditQuoteSessionTime called');
    const existingData = this.getSessionData();
    console.log('🔍 DEBUG: existingData before edit time:', existingData);
    
    if (existingData && existingData.sessionStartTime) {
      const editQuoteStartTime = new Date().toISOString();
      const startTime = new Date(existingData.sessionStartTime);
      const endTime = new Date(editQuoteStartTime);
      const editQuoteSessionTimeInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
      
      const updatedData = {
        ...existingData,
        editQuoteSessionTime: editQuoteSessionTimeInSeconds
      };
      
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updatedData));
      console.log('🔍 DEBUG: updatedData after setting edit time:', updatedData);
      
      console.log('✏️ Edit Quote Session Time Marked:', editQuoteStartTime);
      console.log('✏️ Edit Quote Session Duration:', editQuoteSessionTimeInSeconds.toFixed(2), 'seconds');
      
      return editQuoteSessionTimeInSeconds;
    }
    
    // If no existing session, store current timestamp as session start and edit time
    const now = new Date().toISOString();
    const sessionData = {
      sessionStartTime: now,
      sessionEndTime: null,
      awayTime: 0,
      idleTime: 0,
      tabsCount: 0,
      editQuoteSessionTime: 0 // Edit happened at session start
    };
    sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    console.log('🔍 DEBUG: Created new session data:', sessionData);
    console.log('✏️ Created new session with edit time at start');
    return 0;
  }

  private static startVisibilityTracking(): void {
    if (this.isTracking) return;
    
    this.isTracking = true;
    this.totalAwayTime = 0;
    
    // Listen for visibility change events
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('focus', this.handleWindowFocus);
    
    console.log('👁️ Started visibility tracking for away time');
  }

  private static stopVisibilityTracking(): void {
    if (!this.isTracking) return;
    
    this.isTracking = false;
    
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('focus', this.handleWindowFocus);
    
    console.log('👁️ Stopped visibility tracking');
  }

  private static handleVisibilityChange = (): void => {
    if (document.hidden) {
      // User switched away from tab
      this.awayStartTime = Date.now();
      this.tabSwitchCount++;
      // Pause idle tracking while away - reset idle timer to prevent overlap
      this.lastActivityTime = Date.now();
      console.log('🚪 User went away (tab hidden) - idle tracking paused');
      console.log('🔄 Tab switch count:', this.tabSwitchCount);
    } else {
      // User returned to tab
      if (this.awayStartTime) {
        const awayDuration = Date.now() - this.awayStartTime;
        this.totalAwayTime += awayDuration;
        // Resume idle tracking - reset activity timer to current time
        this.lastActivityTime = Date.now();
        console.log(`🏠 User returned (away for ${(awayDuration / 1000).toFixed(2)}s, total: ${(this.totalAwayTime / 1000).toFixed(2)}s) - idle tracking resumed`);
        this.awayStartTime = null;
      }
    }
  };

  private static handleWindowBlur = (): void => {
    // User switched to another window/application
    if (!this.awayStartTime) {
      this.awayStartTime = Date.now();
      this.tabSwitchCount++;
      // Pause idle tracking while away - reset idle timer to prevent overlap
      this.lastActivityTime = Date.now();
      console.log('🚪 User went away (window blur) - idle tracking paused');
      console.log('🔄 Tab switch count:', this.tabSwitchCount);
    }
  };

  private static handleWindowFocus = (): void => {
    // User returned to window
    if (this.awayStartTime) {
      const awayDuration = Date.now() - this.awayStartTime;
      this.totalAwayTime += awayDuration;
      // Resume idle tracking - reset activity timer to current time
      this.lastActivityTime = Date.now();
      console.log(`🏠 User returned (away for ${(awayDuration / 1000).toFixed(2)}s, total: ${(this.totalAwayTime / 1000).toFixed(2)}s) - idle tracking resumed`);
      this.awayStartTime = null;
    }
  };

  private static startIdleTracking(): void {
    this.lastActivityTime = Date.now();
    this.totalIdleTime = 0;
    
    // Set up activity listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, this.resetIdleTimer, true);
    });
    
    // Check for idle time every 5 seconds
    this.idleCheckInterval = setInterval(this.checkIdleTime, 5000);
    
    console.log('😴 Started idle tracking (30s threshold)');
  }

  private static stopIdleTracking(): void {
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
    
    // Remove activity listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.removeEventListener(event, this.resetIdleTimer, true);
    });
    
    console.log('😴 Stopped idle tracking');
  }

  private static resetIdleTimer = (): void => {
    this.lastActivityTime = Date.now();
  };

  private static checkIdleTime = (): void => {
    // Don't count idle time while user is away
    if (this.awayStartTime) {
      // User is currently away, reset idle timer and don't accumulate idle time
      this.lastActivityTime = Date.now();
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;
    
    // If user has been idle for more than 30 seconds, count it as idle time
    if (timeSinceLastActivity >= this.IDLE_THRESHOLD) {
      const idleTimeToAdd = Math.min(timeSinceLastActivity - this.IDLE_THRESHOLD, 5000); // Max 5s per check
      this.totalIdleTime += idleTimeToAdd;
      this.lastActivityTime = now - this.IDLE_THRESHOLD; // Reset to threshold to avoid double counting
      
      console.log(`😴 Idle time detected: +${(idleTimeToAdd / 1000).toFixed(1)}s (total: ${(this.totalIdleTime / 1000).toFixed(1)}s)`);
    }
  };
}
